/* ============================================================
   NRBY — Scroll Collage controller
   Content-agnostic: transitions between consecutive .collage-scene
   elements. Two drivers and two transition styles:
     drivers:        scroll (pinned) | autoplay (timer, loops)
     transitions:    crossfade (default) | push (tiles slide in)
   Config is read from the first scene that declares it (the hero).

   PERFORMANCE: cell lists and their random offsets are cached at build
   time. Each frame only touches the 1–2 scenes that are actually on
   screen (diff-based show/hide) — never all scenes, never querySelectorAll.
   ============================================================ */
(function () {
  'use strict';

  if (window.__collageScrollInit) return;
  window.__collageScrollInit = true;

  var CONFIG = {
    SCENE_VH: 200,          // svh of scroll length per scene (scroll mode). 100 = one screen.
    FADE_FRACTION: 0.60,    // portion of each scene segment that is the transition (scroll mode)
    STAGGER_SPAN: 0.85,     // 0..0.95 — how SCATTERED the per-tile start times are
    STAGGER_DUR: 0.55,      // 0..1 — how long EACH tile takes to fade (fraction of the window)

    AUTOPLAY: false,        // timer mode on/off
    AUTOPLAY_DELAY: 3000,   // ms each scene holds before crossfading (autoplay)
    CROSSFADE_MS: 1400,     // ms the crossfade itself takes (autoplay)

    SHOW_HEADER: false,     // true → framed mode: theme header visible, coloured frame around grid
    FRAME_PX: 16,           // framed-mode coloured frame thickness (px)
    HEADER_PX: 0,           // framed-mode menu-bar height, desktop (0 = auto-detect)
    HEADER_PX_MOBILE: 60,   // framed-mode menu-bar height, mobile (0 = auto-detect)
    TOP_SAFE: 20,           // framed-mode extra margin above the grid
    BOTTOM_SAFE: 20,        // framed-mode margin below the grid
    REDUCED_MODE: 'full',   // what to do when the OS has "reduce motion" on:
                            //   'full'      → run the effect anyway (default)
                            //   'crossfade' → pin & fade, but no push (gentler)
                            //   'static'    → no pin, plain stacked grids (most accessible)
    TRANSITION: 'crossfade',// 'crossfade' | 'push'
    PUSH_DELAY: 3000,       // ms each scene holds before pushing (autoplay)
    PUSH_SPEED: 1200,       // ms the push itself takes (autoplay)
    PUSH_DISTANCE: 60,      // % of a tile's own size each tile travels in
    PUSH_OUT_PORTION: 0.55, // push: outgoing fade completes by this fraction of t (then it's gone)
    PUSH_IN_LEAD: 0.45,     // push: incoming slide-in waits until this fraction of t (after fade)

    // Per-page title overlay timing (fraction of a transition's progress t)
    TITLE_OUT_END: 0.10,    // outgoing title has fully left by here (just before images fade)
    TITLE_IN_START: 0.30,   // incoming title begins appearing here
    TITLE_IN_END: 0.45,     // incoming title fully shown here (just before the push begins)

    MOBILE_BP: 749
  };

  var state = {
    scenes: [],
    spacer: null,
    backdrop: null,
    titlesLayer: null,
    track: { top: 0, height: 0 },
    rafQueued: false,
    built: false,
    mode: 'scroll',
    activeA: null,
    activeB: null,
    autoplayRAF: null,
    autoplayStart: null,
    shown: [],          // scenes currently visible this frame
    restingIdx: -1,     // index of the scene currently held at rest (-1 = none)
    framed: false,      // framed mode (theme header visible, coloured frame)
    lastPageMarked: false, // the final scene is marked "last page" (slides into footer)
    isMobile: false     // tracks the breakpoint so we can rebuild when it changes
  };

  var prefersReduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- utilities ------------------------------------------ */
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  /* width of the vertical scrollbar (0 on overlay-scrollbar devices like mobile).
     Exposed as --collage-sbw so the CSS can keep the grid centred in the visible
     area instead of the scrollbar-inclusive 100vw. */
  function updateViewportVars() {
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    if (!(sbw >= 0)) sbw = 0;
    document.documentElement.style.setProperty('--collage-sbw', sbw + 'px');
  }

  /* The site's own background colour (body, then html), white as a fallback. */
  function siteBg() {
    var els = [document.body, document.documentElement];
    for (var i = 0; i < els.length; i++) {
      try {
        var c = getComputedStyle(els[i]).backgroundColor;
        if (c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') return c;
      } catch (e) {}
    }
    return '#ffffff';
  }

  /* In framed mode on desktop, drop the stage below the theme header so the
     grid's top frame isn't hidden by the menu. Mobile is left untouched (the
     grid is already clear of the header there). */
  function measureHeaderHeight() {
    var limit = window.innerHeight * 0.5, best = 0, i, el, r;
    var sels = ['.shopify-section-group-header-group', '#shopify-section-header',
      'header[role="banner"]', '.header-wrapper', '.site-header', '.section-header',
      'sticky-header', 'header', '.header', '[id*="header" i]', '[class*="header" i]'];
    for (i = 0; i < sels.length; i++) {
      el = document.querySelector(sels[i]);
      if (!el) continue;
      r = el.getBoundingClientRect();
      // a header sits at the very top; use its BOTTOM edge as the offset
      if (r.height > 0 && r.top <= 12 && r.bottom > best && r.bottom < limit) best = r.bottom;
    }
    if (best) return Math.round(best);
    // fallback: inspect whatever is painted at the top-centre (the header is
    // above our stage in framed mode), then take its container's bottom edge
    try {
      el = document.elementFromPoint(Math.round(window.innerWidth / 2), 6);
      var h = el && el.closest && el.closest('header, [role="banner"], [class*="header" i], [id*="header" i]');
      if (h) {
        r = h.getBoundingClientRect();
        if (r.height > 0 && r.top <= 12 && r.bottom < limit) return Math.round(r.bottom);
      }
    } catch (e) {}
    return 0;
  }
  /* A sticky/fixed footer painted over our stage at the bottom; 0 otherwise. */
  function measureFooterHeight() {
    try {
      var x = Math.round(window.innerWidth / 2);
      var el = document.elementFromPoint(x, window.innerHeight - 6);
      if (!el) return 0;
      if (el.closest && el.closest('.collage-scene, .collage-grid, .collage-titles, .collage-backdrop, .collage-spacer')) return 0;
      var f = (el.closest && el.closest('footer, [role="contentinfo"], [class*="footer" i], [id*="footer" i]')) || el;
      var r = f.getBoundingClientRect();
      if (r.top > window.innerHeight * 0.5 && r.bottom >= window.innerHeight - 4 && r.height < window.innerHeight * 0.5) {
        return Math.round(window.innerHeight - r.top);
      }
    } catch (e) {}
    return 0;
  }

  /* Framed-mode safe areas. Top = (menu-bar height, auto or manual) + a base
     safe margin so the nav never crops the grid AND it isn't edge-to-edge.
     Bottom = base safe margin + any sticky footer. Mobile has its own fixed
     nav (default 60px), so it gets its own menu-bar height. */
  function updateChromeOffsets() {
    var top = 0, bottom = 0;
    if (CONFIG.SHOW_HEADER) {
      var isMobile = window.innerWidth <= CONFIG.MOBILE_BP;
      var manual = isMobile ? CONFIG.HEADER_PX_MOBILE : CONFIG.HEADER_PX;
      var nav = manual > 0 ? manual : measureHeaderHeight();
      top = nav + CONFIG.TOP_SAFE;
      bottom = measureFooterHeight() + CONFIG.BOTTOM_SAFE;
    }
    document.documentElement.style.setProperty('--collage-top', top + 'px');
    document.documentElement.style.setProperty('--collage-bottom', bottom + 'px');
  }

  /* Per-tile progress 0..1. `spreadFactor` scatters the START times; `dur`
     sets how long EACH tile animates. They're decoupled so a wide scatter
     no longer forces a snappy per-tile fade. start + dur stays <= 1 so the
     latest tile still completes by t = 1. */
  function cellProgress(t, r, spreadFactor, dur) {
    var spread = spreadFactor * (1 - dur);
    return clamp((t - r * spread) / dur, 0, 1);
  }
  function vh() { return window.innerHeight; }

  /* ---- read per-page config from the first declaring scene -- */
  function readConfig() {
    // Read from the first config-bearing scene (the hero) in DOM order — NOT the
    // filtered scene list — so global settings survive even if a scene is hidden
    // on the current device.
    var src = document.querySelector('[data-collage-scene][data-autoplay]');
    if (!src) return;
    var d = src.dataset;
    CONFIG.AUTOPLAY = (d.autoplay === 'true');
    if (d.autoplayDelay) CONFIG.AUTOPLAY_DELAY = Math.max(0, parseFloat(d.autoplayDelay) * 1000);
    if (d.crossfade)     CONFIG.CROSSFADE_MS   = Math.max(100, parseFloat(d.crossfade) * 1000);
    if (d.stagger)       CONFIG.STAGGER_SPAN   = clamp(parseFloat(d.stagger) / 100, 0, 0.95);
    if (d.scrollLength)  CONFIG.SCENE_VH       = Math.max(50, parseFloat(d.scrollLength) * 100);
    if (d.fadeFraction)  CONFIG.FADE_FRACTION  = clamp(parseFloat(d.fadeFraction) / 100, 0.1, 0.95);
    if (d.transition)    CONFIG.TRANSITION     = (d.transition === 'push') ? 'push' : 'crossfade';
    if (d.reducedMotion) CONFIG.REDUCED_MODE   = d.reducedMotion; // 'full' | 'crossfade' | 'static'
    if (d.showHeader)    CONFIG.SHOW_HEADER     = (d.showHeader === 'true');
    if (d.frame)         CONFIG.FRAME_PX        = Math.max(0, parseFloat(d.frame));
    if (d.headerOffset)  CONFIG.HEADER_PX       = Math.max(0, parseFloat(d.headerOffset));
    if (d.headerOffsetMobile) CONFIG.HEADER_PX_MOBILE = Math.max(0, parseFloat(d.headerOffsetMobile));
    if (d.topSafe)       CONFIG.TOP_SAFE        = Math.max(0, parseFloat(d.topSafe));
    if (d.bottomSafe)    CONFIG.BOTTOM_SAFE     = Math.max(0, parseFloat(d.bottomSafe));
    if (d.pushDelay)     CONFIG.PUSH_DELAY      = Math.max(0, parseFloat(d.pushDelay) * 1000);
    if (d.pushSpeed)     CONFIG.PUSH_SPEED      = Math.max(100, parseFloat(d.pushSpeed) * 1000);
    if (d.pushDistance)  CONFIG.PUSH_DISTANCE   = Math.max(0, parseFloat(d.pushDistance));
  }

  /* ---- will-change bookkeeping ----------------------------- */
  function markActive(sceneA, sceneB) {
    if (sceneA === state.activeA && sceneB === state.activeB) return;
    if (state.activeA && state.activeA !== sceneA && state.activeA !== sceneB) {
      state.activeA.style.willChange = '';
    }
    if (state.activeB && state.activeB !== sceneA && state.activeB !== sceneB) {
      state.activeB.style.willChange = '';
    }
    if (sceneA) sceneA.style.willChange = 'opacity, transform';
    if (sceneB) sceneB.style.willChange = 'opacity, transform';
    state.activeA = sceneA;
    state.activeB = sceneB;
  }

  /* ---- repeat-fill dense grids ---------------------------- */
  function fillGrid(scene) {
    if (scene.dataset.sceneType !== 'grid') return;
    if (scene.dataset.fill === 'false') return;
    var grid = scene.querySelector('[data-collage-grid]');
    if (!grid) return;

    var target = parseInt(scene.dataset.target, 10) || 0;
    Array.prototype.forEach.call(grid.querySelectorAll('[data-clone]'), function (n) {
      n.parentNode.removeChild(n);
    });

    var originals = Array.prototype.slice.call(grid.querySelectorAll('[data-collage-cell]'));
    if (!originals.length || originals.length >= target) return;

    var lastSrc = null;
    while (grid.querySelectorAll('[data-collage-cell]').length < target) {
      var pick = originals[Math.floor(Math.random() * originals.length)];
      if (originals.length > 1) {
        var guard = 0;
        while (pick === lastSrc && guard < 6) {
          pick = originals[Math.floor(Math.random() * originals.length)];
          guard++;
        }
      }
      lastSrc = pick;
      var clone = pick.cloneNode(true);
      clone.setAttribute('data-clone', '');
      // clear any inline styles copied from a mid-transition source
      clone.style.opacity = '';
      clone.style.transform = '';
      grid.appendChild(clone);
    }
  }

  /* Optional cell distribution: scatter randomly, or keep cells from the same
     block (image/video groups, and their repeat-fill clones) spaced apart so
     they don't sit in a clump. Runs after fill, before the cells are cached. */
  function arrangeCells(scene) {
    if (scene.dataset.sceneType !== 'grid') return;
    var grid = scene.querySelector('[data-collage-grid]');
    if (!grid || grid.querySelector('canvas')) return; // skip single-video grids
    var mode = scene.dataset.cellArrange;
    if (mode !== 'random' && mode !== 'gap') return;

    var cells = Array.prototype.slice.call(grid.querySelectorAll('[data-collage-cell]'));
    if (cells.length < 3) return;
    var arranged, i, j, tmp;

    if (mode === 'random') {
      arranged = cells.slice();
      for (i = arranged.length - 1; i > 0; i--) {
        j = Math.floor(Math.random() * (i + 1));
        tmp = arranged[i]; arranged[i] = arranged[j]; arranged[j] = tmp;
      }
    } else {
      // gap mode — each cell's gap is its block override (data-cell-bgap) if set,
      // otherwise the page default (data-cell-gap). Bail if nothing needs spacing.
      var pageGap = parseInt(scene.dataset.cellGap, 10) || 0;
      var hasBlockGap = false, c0;
      for (c0 = 0; c0 < cells.length; c0++) {
        if ((parseInt(cells[c0].getAttribute('data-cell-bgap'), 10) || 0) > 0) { hasBlockGap = true; break; }
      }
      if (pageGap < 1 && !hasBlockGap) return;

      arranged = [];
      var remaining = cells.slice();
      var lastAt = {};
      while (remaining.length) {
        var pick = 0;
        for (var k = 0; k < remaining.length; k++) {
          var g = remaining[k].getAttribute('data-cell-group') || '';
          var eg = parseInt(remaining[k].getAttribute('data-cell-bgap'), 10) || 0;
          if (eg < 1) eg = pageGap;
          if (lastAt[g] === undefined || (arranged.length - lastAt[g]) > eg) { pick = k; break; }
        }
        var cell = remaining.splice(pick, 1)[0];
        lastAt[cell.getAttribute('data-cell-group') || ''] = arranged.length;
        arranged.push(cell);
      }
    }
    for (i = 0; i < arranged.length; i++) grid.appendChild(arranged[i]);
  }

  /* ---- seed + cache cells (ONCE at build) ----------------- */
  function seedAndCache(scene) {
    var nodes = scene.querySelectorAll('[data-collage-cell]');
    var arr = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!el.dataset.r) el.dataset.r = Math.random().toFixed(4);
      if (!el.dataset.dx || !el.dataset.dy) {
        var angle = Math.random() * Math.PI * 2;
        el.dataset.dx = Math.cos(angle).toFixed(4);
        el.dataset.dy = Math.sin(angle).toFixed(4);
      }
      arr.push({
        el: el,
        r: parseFloat(el.dataset.r) || 0,
        dx: parseFloat(el.dataset.dx) || 0,
        dy: parseFloat(el.dataset.dy) || 0
      });
    }
    scene._cells = arr; // cached list; render never re-queries the DOM
    scene._videos = Array.prototype.slice.call(scene.querySelectorAll('video'));

    // single-video grid: one source video painted into per-tile canvases
    scene._sourceVideo = scene.querySelector('[data-collage-source]') || null;
    scene._videoRestart = (scene.dataset.gridVideoRestart === 'true');
    scene._tileGeomReady = false;
    scene._canvasTiles = null;
    if (scene._sourceVideo) {
      var tiles = [];
      for (var c = 0; c < arr.length; c++) {
        var cv = arr[c].el.querySelector('canvas');
        if (cv) tiles.push({ el: arr[c].el, canvas: cv, ctx: cv.getContext('2d'), sx: 0, sy: 0, sw: 0, sh: 0 });
      }
      scene._canvasTiles = tiles;
    }
  }

  /* Derive the grid's row count from the ACTUAL number of cells (after fill),
     not the Liquid target — otherwise a page with more images than its target
     sizes its cells too tall and the bottom rows overflow off-screen. */
  function setGridRows(scene) {
    if (scene.dataset.sceneType !== 'grid') return;
    var count = scene._cells ? scene._cells.length : 0;
    if (!count) return;
    var colsD = parseInt(scene.dataset.colsDesktop, 10) || 3;
    var colsM = parseInt(scene.dataset.colsMobile, 10) || colsD;
    scene.style.setProperty('--collage-rows', Math.ceil(count / colsD));
    scene.style.setProperty('--collage-rows-mobile', Math.ceil(count / colsM));
  }

  /* ---- per-page titles: lift into an independent overlay layer */
  function buildTitles() {
    if (!state.titlesLayer) {
      state.titlesLayer = document.createElement('div');
      state.titlesLayer.className = 'collage-titles';
      document.body.appendChild(state.titlesLayer);
    }
    for (var i = 0; i < state.scenes.length; i++) {
      var scene = state.scenes[i];
      var t = scene.querySelector('.collage-title');
      if (t) {
        t._homeParent = scene;          // where to put it back on teardown
        state.titlesLayer.appendChild(t);
        t.style.opacity = 0;
        scene._titleEl = t;
      } else {
        scene._titleEl = null;
      }
    }
  }
  function setTitle(scene, o) {
    if (scene._titleEl) scene._titleEl.style.opacity = o;
  }

  /* ---- video playback follows the active scene ------------ */
  function playScene(scene) {
    var vids = scene._videos;
    if (!vids) return;
    // single-video grid set to restart: rewind the source clip on each entry
    if (scene._sourceVideo && scene._videoRestart) {
      try { scene._sourceVideo.currentTime = 0; } catch (e) {}
    }
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      if (v.offsetParent === null) continue; // skip hidden views (display:none), e.g. the off-breakpoint hero video
      v.muted = true;
      var p = v.play();
      if (p && p.catch) p.catch(function () {}); // ignore autoplay rejections
    }
  }
  function pauseScene(scene) {
    var vids = scene._videos;
    if (!vids) return;
    for (var i = 0; i < vids.length; i++) {
      try { vids[i].pause(); } catch (e) {}
    }
  }

  /* ---- single-video grid: paint each tile's slice of one video ---- */
  /* Map the source video over the tiles' bounding box (object-fit: cover),
     then give each tile the source rectangle for its resting position. The
     canvas content rides the cell's push/fade transform like an image would. */
  function computeTileRects(scene) {
    var v = scene._sourceVideo, tiles = scene._canvasTiles;
    if (!v || !tiles || !tiles.length || !v.videoWidth) return false;

    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, i, e;
    for (i = 0; i < tiles.length; i++) {
      e = tiles[i].el;
      if (e.offsetWidth === 0 || e.offsetHeight === 0) return false; // layout not ready
      if (e.offsetLeft < minX) minX = e.offsetLeft;
      if (e.offsetTop < minY) minY = e.offsetTop;
      if (e.offsetLeft + e.offsetWidth > maxX) maxX = e.offsetLeft + e.offsetWidth;
      if (e.offsetTop + e.offsetHeight > maxY) maxY = e.offsetTop + e.offsetHeight;
    }
    var GW = maxX - minX, GH = maxY - minY;
    var vw = v.videoWidth, vh = v.videoHeight;
    var scale = Math.max(GW / vw, GH / vh);     // cover
    var offX = (GW - vw * scale) / 2, offY = (GH - vh * scale) / 2;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);

    for (i = 0; i < tiles.length; i++) {
      var t = tiles[i]; e = t.el;
      t.sx = (e.offsetLeft - minX - offX) / scale;
      t.sy = (e.offsetTop - minY - offY) / scale;
      t.sw = e.offsetWidth / scale;
      t.sh = e.offsetHeight / scale;
      var cw = Math.max(1, Math.round(e.offsetWidth * dpr));
      var ch = Math.max(1, Math.round(e.offsetHeight * dpr));
      if (t.canvas.width !== cw) t.canvas.width = cw;
      if (t.canvas.height !== ch) t.canvas.height = ch;
    }
    scene._tileGeomReady = true;
    return true;
  }

  function drawVideoGrids() {
    for (var i = 0; i < state.shown.length; i++) {
      var scene = state.shown[i];
      var v = scene._sourceVideo;
      if (!v || !scene._canvasTiles || v.readyState < 2) continue;
      if (!scene._tileGeomReady && !computeTileRects(scene)) continue;
      var tiles = scene._canvasTiles;
      for (var j = 0; j < tiles.length; j++) {
        var t = tiles[j];
        try { t.ctx.drawImage(v, t.sx, t.sy, t.sw, t.sh, 0, 0, t.canvas.width, t.canvas.height); }
        catch (e) {}
      }
    }
  }
  function startDrawLoop() {
    if (state.drawRAF) return;
    var loop = function () {
      if (!state.built) { state.drawRAF = null; return; }
      drawVideoGrids();
      state.drawRAF = window.requestAnimationFrame(loop);
    };
    state.drawRAF = window.requestAnimationFrame(loop);
  }
  function hasVideoGrid() {
    for (var i = 0; i < state.scenes.length; i++) if (state.scenes[i]._sourceVideo) return true;
    return false;
  }

  /* ---- build / teardown ----------------------------------- */
  function collectScenes() {
    // Exclude scenes hidden on the current device (CSS display:none via the
    // hide-on-mobile / hide-on-desktop toggles) so they don't occupy a slot in
    // the sequence or the scroll length.
    state.scenes = Array.prototype.slice.call(
      document.querySelectorAll('[data-collage-scene]')
    ).filter(function (s) {
      var disp = '';
      try { disp = getComputedStyle(s).display; } catch (e) {}
      return disp !== 'none';
    });
    state.isMobile = window.innerWidth <= CONFIG.MOBILE_BP;
    var n = state.scenes.length;
    state.lastPageMarked = n > 0 && state.scenes[n - 1].dataset.collageLast === 'true';
  }

  function clearCells(scene) {
    var cells = scene._cells;
    if (!cells) return;
    for (var i = 0; i < cells.length; i++) {
      cells[i].el.style.opacity = '';
      cells[i].el.style.transform = '';
    }
  }

  function hideScene(scene) {
    scene.style.opacity = 0;
    scene.style.visibility = 'hidden';
    scene.dataset.active = 'false';
    clearCells(scene);
    setTitle(scene, 0);
  }

  function teardown() {
    if (state.autoplayRAF) { window.cancelAnimationFrame(state.autoplayRAF); state.autoplayRAF = null; }
    if (state.drawRAF) { window.cancelAnimationFrame(state.drawRAF); state.drawRAF = null; }
    state.autoplayStart = null;
    document.body.classList.remove('collage-active');
    document.body.classList.remove('collage-framed');
    document.documentElement.classList.remove('collage-active-root');
    document.documentElement.style.removeProperty('--collage-frame');
    document.documentElement.style.removeProperty('--collage-top');
    document.documentElement.style.removeProperty('--collage-bottom');

    // fully reset any inline styles we set
    state.scenes.forEach(function (scene) {
      scene.style.opacity = '';
      scene.style.visibility = '';
      scene.style.willChange = '';
      scene.style.transform = '';
      scene.dataset.active = 'false';
      clearCells(scene);
      pauseScene(scene);
    });

    if (state.spacer && state.spacer.parentNode) {
      state.spacer.parentNode.removeChild(state.spacer);
    }
    state.spacer = null;
    if (state.backdrop && state.backdrop.parentNode) {
      state.backdrop.parentNode.removeChild(state.backdrop);
    }
    state.backdrop = null;

    // restore each title to its scene (or drop it if the scene is gone)
    if (state.titlesLayer) {
      state.scenes.forEach(function (scene) {
        var t = scene._titleEl;
        if (t) {
          t.style.opacity = '';
          if (t._homeParent && document.contains(t._homeParent)) t._homeParent.appendChild(t);
          else if (t.parentNode) t.parentNode.removeChild(t);
        }
        scene._titleEl = null;
      });
      if (state.titlesLayer.parentNode) state.titlesLayer.parentNode.removeChild(state.titlesLayer);
      state.titlesLayer = null;
    }

    state.built = false;
    state.shown = [];
    state.restingIdx = -1;
    state.activeA = null;
    state.activeB = null;
  }

  function build() {
    collectScenes();
    if (!state.scenes.length) return;

    readConfig();
    updateViewportVars();

    if (prefersReduced) {
      if (CONFIG.REDUCED_MODE === 'static') {
        // most accessible: no pin, plain stacked grids
        state.mode = 'reduced';
        document.body.classList.add('collage-reduced');
        return;
      }
      if (CONFIG.REDUCED_MODE === 'crossfade') {
        // pin & fade but drop the push (translate) motion
        CONFIG.TRANSITION = 'crossfade';
      }
      // 'full' → fall through and run the effect exactly as configured
    }

    state.scenes.forEach(function (scene) {
      fillGrid(scene);
      arrangeCells(scene);
      seedAndCache(scene);
      setGridRows(scene);
    });

    // start with every video paused; reconcile() plays only the visible scene
    state.scenes.forEach(pauseScene);

    // lift per-page titles into their own overlay layer
    buildTitles();

    state.mode = CONFIG.AUTOPLAY ? 'autoplay' : 'scroll';
    state.shown = [];
    state.restingIdx = -1;
    state.framed = !!CONFIG.SHOW_HEADER;

    // framed mode: show the theme header + confine the coloured bg to a frame
    if (CONFIG.SHOW_HEADER) {
      document.body.classList.add('collage-framed');
      document.documentElement.style.setProperty('--collage-frame', CONFIG.FRAME_PX + 'px');
      updateChromeOffsets();
    }

    if (!state.spacer) {
      state.spacer = document.createElement('div');
      state.spacer.className = 'collage-spacer';
      var first = state.scenes[0];
      first.parentNode.insertBefore(state.spacer, first);
    }

    // opaque backdrop so the theme chrome never shows through mid-transition
    if (!state.backdrop) {
      state.backdrop = document.createElement('div');
      state.backdrop.className = 'collage-backdrop';
      document.body.appendChild(state.backdrop);
    }
    if (CONFIG.SHOW_HEADER) {
      // framed: opaque site background so the framed sides stay clean AND a
      // "last page" in normal flow can't bleed up behind the grid mid-scroll.
      state.backdrop.style.background = siteBg();
    } else {
      var bg = '';
      try { bg = getComputedStyle(state.scenes[0]).getPropertyValue('--collage-bg').trim(); } catch (e) {}
      if (!bg) { try { bg = getComputedStyle(state.scenes[0]).backgroundColor; } catch (e2) {} }
      state.backdrop.style.background = bg || '#ffffff';
    }

    if (state.mode === 'autoplay') {
      state.spacer.style.height = '100svh';
      document.body.classList.add('collage-active');
      document.documentElement.classList.add('collage-active-root');
      updateViewportVars(); // re-measure after the scrollbar is hidden (→ 0)
      state.built = true;
      startAutoplay();
    } else {
      state.spacer.style.height = (state.scenes.length * CONFIG.SCENE_VH) + 'svh';
      document.body.classList.add('collage-active');
      document.documentElement.classList.add('collage-active-root');
      updateViewportVars(); // re-measure after the scrollbar is hidden (→ 0)
      state.built = true;
      measure();
      render();
    }

    // paint single-video grids each frame (only runs if a page uses one)
    if (hasVideoGrid()) startDrawLoop();
  }

  function measure() {
    if (!state.spacer) return;
    var rect = state.spacer.getBoundingClientRect();
    state.track.top = rect.top + window.scrollY;
    state.track.height = state.spacer.offsetHeight;
    // per-scene scroll distance, derived from the real spacer height — unit-agnostic
    state.track.segment = state.track.height / Math.max(1, state.scenes.length);
  }

  /* ---- diff-based show/hide ------------------------------- */
  /* Hide only the scenes that were shown last frame but aren't now. */
  function reconcile(shown) {
    var prev = state.shown;
    // hide + pause scenes that just left
    for (var i = 0; i < prev.length; i++) {
      if (shown.indexOf(prev[i]) === -1) { hideScene(prev[i]); pauseScene(prev[i]); }
    }
    // play videos in scenes that just appeared
    for (var j = 0; j < shown.length; j++) {
      if (prev.indexOf(shown[j]) === -1) playScene(shown[j]);
    }
    state.shown = shown;
  }

  function paintRest(idx, promoteIdx) {
    var c = state.scenes[idx];
    // Optionally keep the upcoming scene's GPU layer warm so the next
    // transition doesn't hitch while a layer is created. The partner is
    // promoted (will-change) but stays hidden until the transition starts.
    var partner = (promoteIdx == null) ? null : state.scenes[promoteIdx];
    // already resting on this exact scene? nothing to repaint.
    if (state.restingIdx === idx && state.shown.length === 1 && state.shown[0] === c) {
      markActive(c, partner);
      return;
    }
    c.style.visibility = '';
    c.style.opacity = 1;
    c.dataset.active = 'true';
    clearCells(c);
    setTitle(c, 1); // title fully visible while the page is held
    markActive(c, partner);
    reconcile([c]);
    state.restingIdx = idx;
  }

  function paintPair(idx, nextIdx, t) {
    state.restingIdx = -1;
    var out = state.scenes[idx];
    var inc = state.scenes[nextIdx];
    out.style.visibility = '';
    inc.style.visibility = '';
    out.dataset.active = 'true';
    inc.dataset.active = 'true';

    if (CONFIG.TRANSITION === 'push') {
      // fade-out leads, push-in trails (with overlap) so arriving tiles
      // don't mask the dissolve beneath them.
      var tOut = clamp(t / CONFIG.PUSH_OUT_PORTION, 0, 1);
      var tIn = clamp((t - CONFIG.PUSH_IN_LEAD) / (1 - CONFIG.PUSH_IN_LEAD), 0, 1);
      staggerOut(out, tOut);
      pushIn(inc, tIn);
    } else {
      staggerOut(out, t);
      staggerIn(inc, t);
    }

    // outgoing title exits quickly (before its images fade); incoming title
    // enters late (so it's up just before the push begins)
    setTitle(out, 1 - easeInOutCubic(clamp(t / CONFIG.TITLE_OUT_END, 0, 1)));
    setTitle(inc, easeInOutCubic(
      clamp((t - CONFIG.TITLE_IN_START) / (CONFIG.TITLE_IN_END - CONFIG.TITLE_IN_START), 0, 1)
    ));

    markActive(out, inc);
    reconcile([out, inc]);
  }

  /* ---- scroll-mode render --------------------------------- */
  function render() {
    if (!state.built || state.mode !== 'scroll') return;
    var n = state.scenes.length;
    var trackEnd = state.track.top + state.track.height;
    var lastScene = state.scenes[n - 1];

    // fully past the experience: clear the stage so the footer is reachable
    if (window.scrollY > trackEnd) {
      if (lastScene) { lastScene.style.transform = ''; lastScene.style.willChange = ''; }
      reconcile([]);
      markActive(null, null);
      state.restingIdx = -1;
      if (state.backdrop) state.backdrop.style.visibility = 'hidden';
      return;
    }

    // "Last page" release: once the final scene is resting, slide it up in sync
    // with the footer rising, so it scrolls away into the footer (instead of the
    // footer painting over a static pinned scene). The final scene still blended
    // in via the normal crossfade just before this.
    if (state.lastPageMarked && lastScene) {
      var releaseStart = trackEnd - vh();
      if (window.scrollY > releaseStart) {
        paintRest(n - 1);
        var off = window.scrollY - releaseStart; // 0 .. vh
        lastScene.style.transform = 'translateY(' + (-off) + 'px)';
        lastScene.style.willChange = 'transform';
        if (state.backdrop) state.backdrop.style.visibility = 'hidden';
        return;
      }
    }

    if (state.backdrop) state.backdrop.style.visibility = '';
    if (lastScene && lastScene.style.transform) { lastScene.style.transform = ''; lastScene.style.willChange = ''; }

    var seg = state.track.segment || vh();
    var raw = (window.scrollY - state.track.top) / seg;
    raw = clamp(raw, 0, n - 0.0001);

    var idx = Math.floor(raw);
    var frac = raw - idx;
    var holdEnd = 1 - CONFIG.FADE_FRACTION;

    if (frac <= holdEnd || idx === n - 1) {
      paintRest(idx);
    } else {
      paintPair(idx, idx + 1, (frac - holdEnd) / CONFIG.FADE_FRACTION);
    }
  }

  /* ---- autoplay-mode loop --------------------------------- */
  function startAutoplay() {
    state.autoplayStart = null;
    var tick = function (now) {
      if (!state.built || state.mode !== 'autoplay') return;
      if (state.autoplayStart === null) state.autoplayStart = now;
      renderAutoplay(now - state.autoplayStart);
      state.autoplayRAF = window.requestAnimationFrame(tick);
    };
    state.autoplayRAF = window.requestAnimationFrame(tick);
  }

  function renderAutoplay(elapsed) {
    var n = state.scenes.length;
    if (n < 2) { paintRest(0); return; }

    var isPush = (CONFIG.TRANSITION === 'push');
    var hold = isPush ? CONFIG.PUSH_DELAY : CONFIG.AUTOPLAY_DELAY;
    var move = isPush ? CONFIG.PUSH_SPEED : CONFIG.CROSSFADE_MS;

    var seg = hold + move;
    var total = n * seg;
    var el = elapsed % total;
    var idx = Math.floor(el / seg);
    var within = el - idx * seg;

    if (within < hold) {
      paintRest(idx, (idx + 1) % n); // pre-promote the next scene during the hold
    } else {
      var t = (within - hold) / move;
      paintPair(idx, (idx + 1) % n, t); // (idx+1)%n loops close→open seamlessly
    }
  }

  /* ---- painters (use cached cell lists; no DOM queries) --- */
  function staggerOut(scene, t) {
    // framed mode: fade the whole layer (incl. the coloured frame) out with the
    // images so the old frame doesn't linger after its images are gone.
    scene.style.opacity = state.framed ? (1 - easeInOutCubic(t)) : 1;
    var cells = scene._cells;
    if (!cells || !cells.length) { scene.style.opacity = 1 - easeInOutCubic(t); return; }
    var span = CONFIG.STAGGER_SPAN, dur = CONFIG.STAGGER_DUR;
    for (var i = 0; i < cells.length; i++) {
      var c = cells[i];
      var local = cellProgress(t, c.r, span, dur);
      c.el.style.opacity = 1 - easeInOutCubic(local);
      c.el.style.transform = ''; // outgoing never translates
    }
  }

  function staggerIn(scene, t) {
    // framed mode: bring the layer (incl. frame) in with the images, not before
    scene.style.opacity = state.framed ? easeInOutCubic(t) : 1;
    var cells = scene._cells;
    if (!cells || !cells.length) { scene.style.opacity = easeInOutCubic(t); return; }
    var span = CONFIG.STAGGER_SPAN * 0.5, dur = CONFIG.STAGGER_DUR;
    for (var i = 0; i < cells.length; i++) {
      var c = cells[i];
      var local = cellProgress(t, c.r, span, dur);
      c.el.style.opacity = easeInOutCubic(local);
      c.el.style.transform = ''; // crossfade does not translate
    }
  }

  function pushIn(scene, t) {
    // Ramp the WHOLE incoming layer (background + tiles) so its opaque
    // background doesn't mask the outgoing fade beneath it. Tiles slide in
    // on a per-tile stagger and ride the layer's opacity ramp.
    scene.style.opacity = easeInOutCubic(t);
    var cells = scene._cells;
    if (!cells || !cells.length) return;
    var span = CONFIG.STAGGER_SPAN * 0.5, dur = CONFIG.STAGGER_DUR;
    var dist = CONFIG.PUSH_DISTANCE;
    for (var i = 0; i < cells.length; i++) {
      var c = cells[i];
      var local = cellProgress(t, c.r, span, dur);
      var move = easeOutCubic(local);          // decelerating settle
      var off = (1 - move) * dist;             // shrinks to 0 as it settles
      c.el.style.transform = 'translate(' + (c.dx * off).toFixed(2) + '%,' + (c.dy * off).toFixed(2) + '%)';
      c.el.style.opacity = ''; // tiles ride the scene-level opacity ramp
    }
  }

  /* ---- scroll / resize loop ------------------------------- */
  function onScroll() {
    if (state.mode !== 'scroll') return;
    if (state.rafQueued) return;
    state.rafQueued = true;
    window.requestAnimationFrame(function () {
      state.rafQueued = false;
      render();
    });
  }
  function onResize() {
    updateViewportVars();
    updateChromeOffsets();
    // Crossing the mobile/desktop breakpoint changes which scenes are hidden
    // (hide-on-mobile / hide-on-desktop), so the visible set + scroll length
    // must be rebuilt from scratch.
    var nowMobile = window.innerWidth <= CONFIG.MOBILE_BP;
    if (nowMobile !== state.isMobile) { rebuild(); return; }
    // tile geometry depends on layout — recompute on next draw
    for (var i = 0; i < state.scenes.length; i++) state.scenes[i]._tileGeomReady = false;
    if (state.mode === 'scroll') { measure(); render(); }
  }

  /* ---- fullscreen (true chromeless; ESC exits) ------------ */
  function fsElement() {
    return document.fullscreenElement || document.webkitFullscreenElement ||
           document.mozFullScreenElement || document.msFullscreenElement || null;
  }
  function requestFs(el) {
    var fn = el.requestFullscreen || el.webkitRequestFullscreen ||
             el.mozRequestFullScreen || el.msRequestFullscreen;
    if (fn) { try { fn.call(el); } catch (e) {} }
  }
  function exitFs() {
    var fn = document.exitFullscreen || document.webkitExitFullscreen ||
             document.mozCancelFullScreen || document.msExitFullscreen;
    if (fn) { try { fn.call(document); } catch (e) {} }
  }
  function onFullscreenClick(e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-collage-fullscreen]') : null;
    if (!btn) return;
    e.preventDefault();
    if (fsElement()) exitFs();
    else requestFs(document.documentElement); // whole page → hides URL/tab chrome
  }

  /* ---- init ----------------------------------------------- */
  function init() {
    if (state.bound) { rebuild(); return; }
    state.bound = true;
    build();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    window.addEventListener('load', function () { updateChromeOffsets(); });
    // chrome heights can settle after fonts/images load — re-measure shortly after
    setTimeout(updateChromeOffsets, 400);
    document.addEventListener('click', onFullscreenClick);
  }

  function rebuild() {
    teardown();
    build();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---- Shopify theme editor resilience -------------------- */
  ['shopify:section:load', 'shopify:section:unload',
   'shopify:section:select', 'shopify:section:deselect'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      clearTimeout(state.editorTimer);
      state.editorTimer = setTimeout(rebuild, 50);
    });
  });
})();
